import { lazy, Suspense } from "react";
import { RouterProvider } from "react-router-dom";
import { mobileRouter } from "./mobile";
import { useAppSelector } from "../hooks/redux";
import { publicRouter } from "./public";
import { setupRouter } from "./setup";
import { ScreenLoaderWhite } from "../components/CustomComponents/ScreenLoaderWhite";
import { useRouterHook } from "../hooks/useRouterHook";
import {
  amplifyProvider,
  authController,
  circadiaEngineProvider,
  clinicController,
  localStorageProvider,
  notificationController,
  patientController,
  timeProvider,
  summaryController,
  homeHealthController,
  pushNotificationProvider,
  smsController,
} from "../AppController";
import { appNotification } from "../util/appNotification";
import useSWRImmutable from "swr/immutable";
import { restrictedRouter } from "./restricted";
import { isPIN } from "../util/pin";
import VitalsRedirectHandler from "../components/VitalsRedirectHandler";
import { useVitalsSubdomainRedirect } from "../hooks/useVitalsSubdomainRedirect";
import { ModeSwitchOverlay } from "../components/CustomComponents/ModeSwitchOverlay";
import { DEBUG_BYPASS_ACCESS_RESTRICTED } from "../pages/PatientProfile-v3/components/DebugConstants";

/** Cognito groups that grant access to each app shell. */
const SETUP_APP_GROUP = "setup_app_user";
const PATIENT_APP_GROUP = "home_health_patient";

const ModeChooser = lazy(() => import("../pages/ModeChooser"));

export const AppRouter = () => {
  // Handle vitals subdomain redirect - backup check
  useVitalsSubdomainRedirect();

  const { getAuthState, populateAppData } = useRouterHook({
    amplifyProvider: amplifyProvider,
    authController: authController,
    clinicController: clinicController,
    localStorageProvider: localStorageProvider,
    notificationController: notificationController,
    timeProvider: timeProvider,
    appNotification: appNotification,
    patientController: patientController,
    circadiaEngineProvider,
    summaryController,
    homeHealthController,
    isPIN,
    smsController: smsController,
  });
  const isBiometric = localStorageProvider.get("isBiometric");
  const getAuthStateP = async () => {
    if (isPIN()) {
      // Mobile flow (PIN/biometric)
      const authState = await getAuthState();

      if (isBiometric && isBiometric === "true" && authState === "biometric") {
        await populateAppData();
      }
    } else {
      // Web flow (no PIN/biometric)
      await getAuthState();
      await populateAppData();
      await pushNotificationProvider.registerDeviceForPushNotifications();
    }
  };
  useSWRImmutable("getAuthState", getAuthStateP);

  const { isAuthorized, isPinAuthorized, hasNoAccess, appMode, userGroups } =
    useAppSelector((state) => {
      return {
        isAuthorized: state.auth.isAuthorized,
        isPinAuthorized: state.auth.isPinAuthorized,
        hasNoAccess: state.auth.hasNoAccess,
        appMode: state.auth.appMode,
        userGroups: state.auth.user?.groups ?? [],
      };
    });
  if (!isAuthorized || (isPIN() ? !isPinAuthorized : false)) {
    return (
      <>
        <RouterProvider router={publicRouter} />
        <ModeSwitchOverlay />
      </>
    );
  }

  if (hasNoAccess && !DEBUG_BYPASS_ACCESS_RESTRICTED) {
    return (
      <>
        <RouterProvider router={restrictedRouter} />
        <ModeSwitchOverlay />
      </>
    );
  }

  const hasSetupAccess = userGroups.includes(SETUP_APP_GROUP);
  const hasPatientAccess = userGroups.includes(PATIENT_APP_GROUP);

  // Dual-access users with no choice persisted yet → show ModeChooser.
  if (hasSetupAccess && hasPatientAccess && !appMode) {
    return (
      <>
        <Suspense fallback={<ScreenLoaderWhite />}>
          <ModeChooser />
        </Suspense>
        <ModeSwitchOverlay />
      </>
    );
  }

  // Setup mode: requires the setup group AND an explicit choice (or no patient access at all).
  if (hasSetupAccess && (appMode === "setup" || !hasPatientAccess)) {
    return (
      <>
        <RouterProvider router={setupRouter} />
        <ModeSwitchOverlay />
      </>
    );
  }

  // circadiaEngineProvider.changeAppTopAndBottom("#f5f5f5", "#fff");
  return (
    <>
      <VitalsRedirectHandler />
      <RouterProvider router={mobileRouter} />
      <ModeSwitchOverlay />
    </>
  );
};
